import React, {useState} from 'react';
import {
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import DateTimePicker, {
  type DateTimePickerEvent,
} from '@react-native-community/datetimepicker';

import {formatDayMinutes} from '@/shared/lib/format';
import {Icon} from '@/shared/ui/icons';
import {useBreakpoint} from '@/shared/lib/breakpoints';
import {AppText} from '@/shared/ui/text';
import {controls, radii, useTheme} from '@/shared/ui/theme';

/**
 * A tap-to-open time field. Holds a minute-of-day (0..1439) and shows the native clock
 * picker — the phone's own wheel/dial, so no locale-parsing of "HH:MM" text and no way
 * to enter 25:70. 24-hour, because the whole app reads schedules that way.
 *
 * On Android the picker is a modal dialog that dismisses itself; we mirror that by
 * closing on any callback. `event.type === 'set'` means the user confirmed a time;
 * 'dismissed' leaves the value untouched.
 */
function minutesToDate(minutes: number): Date {
  const d = new Date();
  d.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
  return d;
}

const styles = StyleSheet.create({
  field: {gap: 6},
  // Deliberately the same box as AppInput at rest — a time IS a field, and the only
  // thing that should tell them apart is the clock glyph and that tapping it opens a
  // picker instead of the keyboard.
  box: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: controls.lg,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: radii.md,
    borderWidth: 1,
  },
  boxMd: {minHeight: controls.md, paddingVertical: 4},
  value: {flex: 1},
});

export function TimeField({
  label,
  accessibilityLabel,
  minutes,
  onChange,
  style,
}: {
  /** Visible caption above the box. Omit it in a table row whose column says it. */
  label?: string;
  /** Required when `label` is omitted: a clock with no name is invisible to a reader. */
  accessibilityLabel?: string;
  /** Minute-of-day 0..1439. */
  minutes: number;
  onChange: (minutes: number) => void;
  style?: StyleProp<ViewStyle>;
}) {
  const p = useTheme();
  const {isDesktop} = useBreakpoint();
  const [open, setOpen] = useState(false);

  const handle = (event: DateTimePickerEvent, date?: Date) => {
    setOpen(false);
    if (event.type === 'set' && date) {
      onChange(date.getHours() * 60 + date.getMinutes());
    }
  };

  return (
    <View style={[styles.field, style]}>
      {label ? <AppText variant="label">{label}</AppText> : null}
      <Pressable
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? label}
        style={({pressed}) => [
          styles.box,
          isDesktop && styles.boxMd,
          {backgroundColor: pressed ? p.muted : p.card, borderColor: p.input},
        ]}>
        <Icon name="clock" size={16} color={p.mutedForeground} />
        <AppText variant={isDesktop ? 'mono' : 'monoLg'} style={styles.value}>
          {formatDayMinutes(minutes) ?? '--:--'}
        </AppText>
      </Pressable>
      {open ? (
        <DateTimePicker
          value={minutesToDate(minutes)}
          mode="time"
          is24Hour
          onChange={handle}
        />
      ) : null}
    </View>
  );
}
